/**
 * Venue service for managing therapy rooms and locations
 */
import StorageService from './StorageService';
import Venue, { VENUE_TYPES } from '../models/Venue';

class VenueService {
  /**
   * Constructor
   */
  constructor() {
    this.storageService = new StorageService('venues');
  }

  /**
   * Get all venues
   * @returns {Array} All venues
   */
  getAllVenues() {
    return this.storageService.getAllAsArray().map(venue => 
      Venue.fromJSON(venue)
    );
  }

  /**
   * Get active venues
   * @returns {Array} All active venues
   */
  getActiveVenues() {
    return this.storageService.find(venue => venue.isActive)
      .map(venue => Venue.fromJSON(venue));
  }

  /**
   * Get venue by ID
   * @param {string} id - Venue ID
   * @returns {Venue|null} Venue or null if not found
   */
  getVenueById(id) {
    const venueData = this.storageService.getById(id);
    if (!venueData) return null;
    
    return Venue.fromJSON(venueData);
  }

  /**
   * Create venue
   * @param {Object} venueData - Venue data
   * @returns {Venue} Created venue
   */
  createVenue(venueData) {
    const venue = new Venue(venueData);
    this.storageService.save(venue.id, venue.toJSON());
    return venue;
  }

  /**
   * Update venue
   * @param {string} id - Venue ID
   * @param {Object} venueData - Venue data to update
   * @returns {Venue|null} Updated venue or null if not found
   */
  updateVenue(id, venueData) {
    const existingVenueData = this.storageService.getById(id);
    if (!existingVenueData) return null;
    
    const venue = Venue.fromJSON(existingVenueData);
    venue.update(venueData);
    
    this.storageService.save(venue.id, venue.toJSON());
    return venue;
  }

  /**
   * Delete venue
   * @param {string} id - Venue ID
   * @returns {boolean} Whether deletion was successful
   */
  deleteVenue(id) {
    return this.storageService.delete(id);
  }

  /**
   * Search venues by text query
   * @param {string} query - Search query
   * @returns {Array} Matching venues
   */
  searchVenues(query) {
    if (!query) return this.getAllVenues();
    
    const lowercasedQuery = query.toLowerCase();
    
    return this.storageService.find(venue => {
      // Search in various fields
      return (
        venue.name?.toLowerCase().includes(lowercasedQuery) ||
        venue.description?.toLowerCase().includes(lowercasedQuery) ||
        venue.location?.toLowerCase().includes(lowercasedQuery) ||
        (venue.notes && venue.notes.toLowerCase().includes(lowercasedQuery))
      );
    }).map(venue => Venue.fromJSON(venue));
  }

  /**
   * Filter venues by type
   * @param {string} type - Venue type
   * @returns {Array} Venues of specified type
   */
  filterByType(type) {
    if (!type) return this.getAllVenues();
    
    return this.storageService.find(venue => venue.type === type)
      .map(venue => Venue.fromJSON(venue));
  }

  /**
   * Filter venues by capacity
   * @param {number} minCapacity - Minimum capacity
   * @returns {Array} Venues with at least the specified capacity
   */
  filterByCapacity(minCapacity) {
    return this.storageService.find(venue => venue.capacity >= minCapacity)
      .map(venue => Venue.fromJSON(venue));
  }

  /**
   * Check if venue is available at a specific time
   * @param {string} venueId - Venue ID
   * @param {Date} dateTime - Date and time to check
   * @returns {boolean} Whether venue is available
   */
  isVenueAvailableAt(venueId, dateTime) {
    const venue = this.getVenueById(venueId);
    if (!venue) return false;
    
    return venue.isAvailableAt(dateTime);
  }

  /**
   * Block time for venue maintenance or other reasons
   * @param {string} venueId - Venue ID
   * @param {Date|string} startTime - Block start time
   * @param {Date|string} endTime - Block end time
   * @param {string} [reason=''] - Reason for blocking
   * @returns {Venue|null} Updated venue or null if not found
   */
  blockVenueTime(venueId, startTime, endTime, reason = '') {
    const venue = this.getVenueById(venueId);
    if (!venue) return null;
    
    venue.blockTime(startTime, endTime, reason);
    this.storageService.save(venue.id, venue.toJSON());
    
    return venue;
  }

  /**
   * Schedule venue maintenance
   * @param {string} venueId - Venue ID
   * @param {Date|string} startDate - Maintenance start date
   * @param {Date|string} endDate - Maintenance end date
   * @param {string} [description='Scheduled maintenance'] - Maintenance description
   * @returns {Venue|null} Updated venue or null if not found
   */
  scheduleVenueMaintenance(venueId, startDate, endDate, description = 'Scheduled maintenance') {
    const venue = this.getVenueById(venueId);
    if (!venue) return null;
    
    venue.scheduleMaintenance(startDate, endDate, description);
    this.storageService.save(venue.id, venue.toJSON());
    
    return venue;
  }

  /**
   * Add equipment to venue
   * @param {string} venueId - Venue ID
   * @param {string} equipmentName - Equipment name
   * @param {string} [details=''] - Equipment details
   * @returns {Venue|null} Updated venue or null if not found
   */
  addEquipmentToVenue(venueId, equipmentName, details = '') {
    const venue = this.getVenueById(venueId);
    if (!venue) return null;
    
    venue.addEquipment(equipmentName, details);
    this.storageService.save(venue.id, venue.toJSON());
    
    return venue;
  }

  /**
   * Remove equipment from venue
   * @param {string} venueId - Venue ID
   * @param {string} equipmentName - Equipment name
   * @returns {Venue|null} Updated venue or null if equipment was removed, null if venue not found
   */
  removeEquipmentFromVenue(venueId, equipmentName) {
    const venue = this.getVenueById(venueId);
    if (!venue) return null;
    
    const wasRemoved = venue.removeEquipment(equipmentName);
    if (wasRemoved) {
      this.storageService.save(venue.id, venue.toJSON());
      return venue;
    }
    
    return venue; // Equipment not found but venue exists
  }

  /**
   * Update venue availability schedule
   * @param {string} venueId - Venue ID
   * @param {Object} availability - Availability schedule
   * @returns {Venue|null} Updated venue or null if not found
   */
  updateVenueAvailability(venueId, availability) {
    const venue = this.getVenueById(venueId);
    if (!venue) return null;
    
    venue.update({ availability });
    this.storageService.save(venue.id, venue.toJSON());
    
    return venue;
  }

  /**
   * Get all unique venue types used in the system
   * @returns {Array} All unique venue types
   */
  getAllVenueTypes() {
    const typesSet = new Set();
    
    this.getAllVenues().forEach(venue => {
      if (venue.type) {
        typesSet.add(venue.type);
      }
    });
    
    return Array.from(typesSet).sort();
  }

  /**
   * Get venues with upcoming maintenance
   * @param {number} [days=30] - Number of days ahead to check
   * @returns {Array} Venues with upcoming maintenance
   */
  getVenuesWithUpcomingMaintenance(days = 30) {
    const now = new Date();
    const future = new Date();
    future.setDate(future.getDate() + days);
    
    return this.getAllVenues().filter(venue => {
      if (!venue.maintenanceSchedule || venue.maintenanceSchedule.length === 0) {
        return false;
      }
      
      // Check if any maintenance schedule overlaps with the date range
      return venue.maintenanceSchedule.some(maintenance => {
        const maintenanceStart = new Date(maintenance.startDate);
        const maintenanceEnd = new Date(maintenance.endDate);
        
        return (
          (maintenanceStart >= now && maintenanceStart <= future) || // Starts within range
          (maintenanceEnd >= now && maintenanceEnd <= future) || // Ends within range
          (maintenanceStart <= now && maintenanceEnd >= future) // Spans entire range
        );
      });
    });
  }

  /**
   * Get total number of venues
   * @returns {number} Venue count
   */
  getVenueCount() {
    return this.storageService.count();
  }

  /**
   * Import venues from JSON
   * @param {string} jsonData - JSON string of venue data
   * @param {boolean} [merge=false] - Whether to merge with existing data
   * @returns {boolean} Whether import was successful
   */
  importVenues(jsonData, merge = false) {
    return this.storageService.importData(jsonData, merge);
  }

  /**
   * Export venues as JSON
   * @returns {string} JSON string of venue data
   */
  exportVenues() {
    return this.storageService.exportData();
  }
}

// Create singleton instance
const venueService = new VenueService();

export default venueService;